import { stringNormalize } from 'quill-string-normalizer';
import * as React from 'react';
import { stripHtml } from "string-strip-html";
import * as _ from 'underscore';

import { FillInBlankPromptWithReadAloud } from './fillInBlankPromptWithReadAloud';

import { ReadAloudSource } from '../readAloud/readAloudSource';
import { CLIP_SOURCE_TYPE, clipSource } from '../../libs/speech/clipSource';
import staticStrings from '../../libs/speech/staticStrings.json';
import Cues from '../renderForQuestions/cues';
import { cancelSpeech } from '../../libs/speech/speechManager';
import { checkCapitalization, checkFillInTheBlankQuestion, findMatchingCueTemplate } from '../../quill-marking-logic/src/main';
import {
  ALLOWED_ATTEMPTS,
  ENTER,
  Feedback,
  FinalAttemptFeedback,
  Prompt,
  fillInBlankInputLabel,
  fillInBlankInputStyle,
  getLatestAttempt,
  hashToCollection,
  maxAttemptsForGrammarFillInBlank,
  renderExplanation,
  renderPreviewFeedback,
  splitPromptForFillInBlank,
} from '../../index';
import { CONNECT, DIAGNOSTIC, GRAMMAR, TURK } from '../../utils/constants';
import { ENGLISH } from '../../utils/languageList';
import { numberOfBlanksForFillInBlankPrompt, pluralize } from '../../libs/index';
import { EXTRA_SMALL_ICON_BASE_SRC,  } from '../../utils/constants';
import { CONCEPT_FEEDBACK_TYPE } from '../../libs/speech/clipSource';

const UNMATCHED_OPTION_KEY = 'fillInBlank.unmatchedOption';
const UNMATCHED_OPTION_OR_BLANK_KEY = 'fillInBlank.unmatchedOptionOrBlank';

export interface PlayFillInBlankQuestionProps {
  // Required for both modes
  dispatch: any;
  isLastQuestion: boolean;
  languageLocale: string;
  nextQuestion: () => void;
  previewMode: boolean;
  question: any;
  setResponse: (response: any) => void;
  submitResponse?: (response: any) => void;

  // Mode prop
  mode: typeof CONNECT | typeof DIAGNOSTIC | typeof GRAMMAR | typeof TURK;

  // Connect- and Grammar-specific (optional)
  concepts?: any;
  conceptsFeedback?: any;
  language?: string;
  showTranslation?: boolean;
  translate?: (key: string) => string;

  // Diagnostic-specific (optional)
  currentKey?: string;
  diagnosticID?: string;

  // Shared but used differently
  FeedbackContainer?: any;
  getGradedResponsesWithCallback?: (questionKey: string, callback: (data: any) => void) => void;
  updateResponseResource?: (response: any, key: string, attempts: any, dispatch: any) => void;
}

interface PlayFillInBlankQuestionState {
  blankAllowed?: boolean;
  cues?: string[];
  inputErrors?: any;
  inputVals?: any;
  responses?: any;
  showExample?: boolean;
  splitPrompt?: string[];
}

const conceptFeedbackType = (mode) => (mode === GRAMMAR ? CONCEPT_FEEDBACK_TYPE.FILL_IN_BLANK : mode)

const maxAttempts = (mode, question) => {
  if ([TURK, DIAGNOSTIC].includes(mode)) { return 1 }

  if (mode === CONNECT) { return ALLOWED_ATTEMPTS }

  if (mode === GRAMMAR) {
    return maxAttemptsForGrammarFillInBlank(question.cues.length, numberOfBlanksForFillInBlankPrompt(question.prompt), question.blankAllowed)
  }
}

export class PlayFillInBlankQuestion extends React.Component<PlayFillInBlankQuestionProps, PlayFillInBlankQuestionState> {

  state = {
    blankAllowed: false,
    cues: [],
    inputErrors: {},
    inputVals: [],
    responses: null,
    showExample: true,
    splitPrompt: null,
  }

  componentDidMount() {
    const { mode, question } = this.props;
    this.setQuestionValues(question);

    // Prevent paste events (both modes)
    if (window.location.href.includes('admin')) { return }

    window.addEventListener('paste', this.preventPaste, true);
  }

  componentDidUpdate(prevProps: PlayFillInBlankQuestionProps) {
    const { question } = this.props;
    if (prevProps.question.prompt !== question.prompt || prevProps.question.key !== question.key) {
      this.setQuestionValues(question);
    }
  }

  componentWillUnmount() {
    this.responseRequestId += 1;
    window.removeEventListener('paste', this.preventPaste, true);
  }

  responseRequestId = 0;

  preventPaste = (e) => {
    e.preventDefault();
    return false;
  }

  correctResponse = () => {
    const { question } = this.props;
    const { responses } = this.state;
    let text;

    if (responses && Object.keys(responses).length) {
      const responseArray = hashToCollection(responses).sort((a: any, b: any) => b.count - a.count);
      const firstOptimalResponse = responseArray.find((r: any) => r.optimal);
      if (firstOptimalResponse) {
        text = firstOptimalResponse.text;
      }
    }
    if (!text) {
      text = question?.answers?.[0]?.text?.replace(/{|}/gm, '');
    }
    return text;
  }

  setQuestionValues = (question: any) => {
    const splitPrompt = splitPromptForFillInBlank(question.prompt);
    this.responseRequestId += 1;

    this.setState({
      blankAllowed: question.blankAllowed,
      cues: question.cues,
      inputErrors: {},
      inputVals: this.generateInputs(numberOfBlanksForFillInBlankPrompt(question.prompt)),
      responses: null,
      splitPrompt,
    }, () => this.getGradedResponsesWithCallback(question));
  }

  getGradedResponsesWithCallback = (question: any) => {
    const { getGradedResponsesWithCallback } = this.props;
    const requestId = this.responseRequestId;
    if (getGradedResponsesWithCallback) {
      getGradedResponsesWithCallback(
        question.key,
        (data: any) => {
          if (requestId !== this.responseRequestId) { return; }
          this.setState({ responses: data });
        }
      );
    }
  }

  getInstructionText = () => {
    const { question } = this.props;

    // Connect mode
    let instructions: JSX.Element | string = 'Fill in the blanks with the word or phrase that best fits the sentence.';
    const latestAttempt = getLatestAttempt(question.attempts);
    if (latestAttempt && latestAttempt.response && latestAttempt.response.feedback) {
      const component = <span dangerouslySetInnerHTML={{ __html: latestAttempt.response.feedback }} />;
      instructions = latestAttempt.response.feedback ? component :
        'Revise your work. Fill in the blanks with the word or phrase that best fits the sentence.';
    } else if (question.instructions && question.instructions !== '') {
      instructions = question.instructions;
    }
    return instructions;
  }

  renderDiagnosticInstructionsFeedback = () => {
    const { language, question, translate, languageLocale } = this.props;
    const { instructions } = question;

    if (!language) {
      return <Feedback feedbackHtml={instructions} feedbackType="instructions" />;
    }
    const translatedFeedbackText = language !== ENGLISH ? translate(`instructions^${instructions}`) : undefined;
    return (
      <Feedback
        feedbackText={instructions}
        feedbackType="instructions"
        language={language}
        languageLocale={languageLocale}
        translatedFeedbackText={translatedFeedbackText}
      />
    );
  }

  generateInputs(numberOfInputVals: number) {
    const { question } = this.props;
    // Seed each blank from the latest attempt so a resumed session (or a
    // teacher preview switching questions) shows the student's last answer
    // instead of empty inputs sitting under that attempt's per-blank feedback.
    const latestAttempt = getLatestAttempt(question.attempts);
    const savedInputs = latestAttempt && latestAttempt.response && latestAttempt.response.inputs;
    const inputs: Array<string> = [];
    for (let i = 0; i < numberOfInputVals; i += 1) {
      inputs.push((savedInputs && savedInputs[i]) || '');
    }
    return inputs;
  }

  handleChange = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { inputVals } = this.state;
    const existing = [...inputVals];
    existing[i] = e.target.value;
    this.setState({
      inputVals: existing,
    });
  }

  getChangeHandler = (index: number) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      this.handleChange(index, e);
    };
  }

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== ENTER) { return }

    e.preventDefault();
    this.handleSubmitClick();
  }

  previousAttemptInputs = (): string[][] => {
    const { question } = this.props;
    if (!question.attempts) { return []; }
    return question.attempts
      .map((attempt: any) => attempt?.response?.inputs)
      .filter((inputs: any) => Array.isArray(inputs));
  }

  inputsMatchPreviousAttempt = (): boolean => {
    const { inputVals } = this.state;
    return this.previousAttemptInputs().some((prev: string[]) =>
      prev.length === inputVals.length && prev.every((val, i) => val === inputVals[i])
    );
  }

  renderText(text: string, i: number): JSX.Element[] {
    const className = text.length > 0 ? 'fill-in-blank-prompt-text' : '';
    const textArray = text.split(' ');
    const spanArray: JSX.Element[] = [];
    textArray.forEach((word, index) => {
      spanArray.push(<span className={className} key={`${i}-${index}`}>{word}</span>);
    });
    return spanArray;
  }

  validateAllInputs = () => {
    const { blankAllowed, cues, inputErrors, inputVals } = this.state;
    const newErrors = inputErrors;
    for (let i = 0; i < inputVals.length; i++) {
      const inputVal = inputVals[i] || '';
      const inputSufficient = blankAllowed || inputVal;
      const cueMatch = (inputVal && cues.some(c => stringNormalize(c).toLowerCase() === stringNormalize(inputVal).toLowerCase().trim())) || (inputVal === '' && blankAllowed);

      if (inputSufficient && cueMatch) {
        delete newErrors[i];
      } else {
        newErrors[i] = true;
      }
    }

    // following condition will return false if no new errors
    if (_.size(newErrors)) {
      const newInputVals = inputVals;
      this.setState({ inputErrors: newErrors, inputVals: newInputVals });
    } else {
      this.setState({ inputErrors: newErrors });
    }
    return Promise.resolve(true);
  }

  getInputProperties = (i: number) => {
    const { mode, question } = this.props;
    const { inputVals } = this.state;
    const latestAttempt = getLatestAttempt(question.attempts);

    const maxAttemptsReached = question.attempts && question.attempts.length === maxAttempts(mode, question);
    const responseOptimal = latestAttempt && latestAttempt.response && latestAttempt.response.optimal;

    return {
      disabled: maxAttemptsReached || responseOptimal,
      value: inputVals[i],
    };
  }

  renderInput = (i: number) => {
    const { question, mode, } = this.props
    const { blankByBlankGrading, attempts, } = question
    const { blankAllowed, cues, inputErrors, inputVals } = this.state;
    let className = 'fill-in-blank-input';
    if (inputErrors[i]) {
      className += ' error';
    }

    const inputProperties = this.getInputProperties(i);
    const value = inputProperties.value;
    const totalBlanks = inputVals.length;

    let icon

    if (blankByBlankGrading && attempts?.length) {
      const lastAttempt = attempts[attempts.length - 1]
      const lastAttemptInputs = lastAttempt.response?.inputs

      // Only show per-blank feedback if we have the inputs array
      if (lastAttemptInputs && blankByBlankGrading) {
        const inputForLastAttempt = lastAttemptInputs[i]?.trim() || ''
        const cueTemplates = blankByBlankGrading[`blank${i + 1}`] || []
        const matchedTemplate = findMatchingCueTemplate(inputForLastAttempt, cueTemplates, question.caseInsensitive)

        // we have special logic in the grading where, if a case-insensitive question with only one attempt is optimal except for casing, the response is considered optimal regardless of case
        const capitalizationCorrect = !question.caseInsensitive || maxAttempts(mode, question) === 1 || (matchedTemplate && checkCapitalization(inputForLastAttempt, matchedTemplate.cue))

        if (matchedTemplate?.optimal && capitalizationCorrect) {
          className += ' correct'
          icon = <img alt="" aria-hidden="true" src={`${EXTRA_SMALL_ICON_BASE_SRC}/check-color.svg`} />
        } else {
          className += ' incorrect'
          icon = <img alt="" aria-hidden="true" src={`${EXTRA_SMALL_ICON_BASE_SRC}/xmark-color.svg`} />
        }
      }
    } else if (!blankByBlankGrading && attempts?.length) {
      const lastAttempt = attempts[attempts.length - 1]
      const maxAttemptsReached = attempts.length === maxAttempts(mode, question)
      if (lastAttempt.response && !lastAttempt.response.optimal && !maxAttemptsReached) {
        className += ' revise'
      }
    }

    return (
      <span className="input-and-icon-wrapper">
        <input
          aria-describedby="cues-container"
          aria-label={fillInBlankInputLabel(i + 1, totalBlanks, blankAllowed)}
          autoComplete="off"
          className={className}
          disabled={inputProperties.disabled}
          id={`input${i}`}
          key={i + 100}
          onChange={this.getChangeHandler(i)}
          onKeyDown={this.handleKeyDown}
          style={fillInBlankInputStyle(value, cues)}
          type="text"
          value={value}
        />
        {icon}
      </span>
    )
  }

  getNegativeConceptResultsForResponse(conceptResults: object[]) {
    return hashToCollection(conceptResults).filter(cr => !cr.correct);
  }

  getNegativeConceptResultForResponse(conceptResults: object[]) {
    const negCRs = this.getNegativeConceptResultsForResponse(conceptResults);
    return negCRs.length > 0 ? negCRs[0] : undefined;
  }

  renderConceptExplanation = () => {
    const { conceptsFeedback, mode, question, showTranslation, languageLocale } = this.props;
    const explanationFor = (key: string) => renderExplanation({ activityType: conceptFeedbackType(mode), conceptsFeedback, data: conceptsFeedback.data[key], key, languageLocale, showTranslation, });

    // Only for Connect and Grammar modes
    if ((mode !== CONNECT && mode !== GRAMMAR) || !conceptsFeedback) {
      return null;
    }

    const latestAttempt: any = getLatestAttempt(question.attempts);

    if (!latestAttempt?.response || latestAttempt?.response?.optimal) {
      return null;
    }

    if (latestAttempt.response.conceptResults) {
      const conceptID = this.getNegativeConceptResultForResponse(latestAttempt.response.conceptResults);
      if (conceptID) {
        const key = conceptID.conceptUID;
        return explanationFor(key);
      }
    } else if (latestAttempt.response.concept_results) {
      const conceptID = this.getNegativeConceptResultForResponse(latestAttempt.response.concept_results);
      if (conceptID) {
        const key = conceptID.conceptUID;
        return explanationFor(key);
      }
    } else if (question && question.modelConceptUID) {
      const key = question.modelConceptUID;
      return explanationFor(key);
    } else if (question.conceptID) {
      const key = question.conceptID;
      return explanationFor(key);
    }

    return null;
  }

  getPromptElements = () => {
    const { splitPrompt } = this.state;
    if (splitPrompt) {
      const l = splitPrompt.length;
      const splitPromptWithInput: JSX.Element[] = [];
      splitPrompt.forEach((section, i) => {
        if (i !== l - 1) {
          splitPromptWithInput.push(...this.renderText(section, i));
          splitPromptWithInput.push(this.renderInput(i));
        } else {
          splitPromptWithInput.push(...this.renderText(section, i));
        }
      });
      return _.flatten(splitPromptWithInput);
    }
  }

  zipInputsAndText = () => {
    const { inputVals, splitPrompt } = this.state;
    const trimmedInputVals = inputVals.map(iv => iv.trim());
    const zipped = _.zip(splitPrompt, trimmedInputVals);
    const formatted = _.flatten(zipped).join('').trim();

    // Always use stripHtml for both modes (safe for plain text, necessary for HTML prompts)
    return stripHtml(formatted).result;
  }

  handleSubmitClick = () => {
    if (this.inputsMatchPreviousAttempt()) { return; }
    cancelSpeech();
    this.validateAllInputs().then(() => {
      const { isLastQuestion, mode, previewMode, question, submitResponse, nextQuestion, } = this.props;
      const { caseInsensitive, conceptID, key, blankByBlankGrading, } = question;
      const { inputErrors, inputVals, responses } = this.state;
      const noErrors = _.size(inputErrors) === 0;

      if (noErrors && responses) {
        const zippedAnswer = this.zipInputsAndText();
        const questionUID = key;
        const defaultConceptUID = conceptID;
        const responsesArray = hashToCollection(responses);
        const isDiagnosticFIB = mode === DIAGNOSTIC || mode === TURK;

        const response = {
          response: checkFillInTheBlankQuestion(
            questionUID,
            zippedAnswer,
            responsesArray,
            caseInsensitive,
            defaultConceptUID,
            isDiagnosticFIB,
            blankByBlankGrading,
            inputVals,
            maxAttempts(mode, question)
          ),
        };

        // Always store the individual input values for per-blank feedback
        (response.response as any).inputs = inputVals;

        this.updateResponseResource(response);
        this.setResponse(response);
        submitResponse(response);

        if (mode === DIAGNOSTIC || mode === TURK) {
          // Don't submit the last question if in previewMode (Diagnostic only, Turk has no preview)
          if (previewMode && isLastQuestion) {
            return;
          }
          nextQuestion();
        }
      }
    });
  }

  setResponse(response: any) {
    const { setResponse } = this.props;
    if (setResponse) {
      setResponse(response);
    }
  }

  updateResponseResource(response: any) {
    const { dispatch, question, updateResponseResource } = this.props;
    const attempts = question.attempts;
    // Use the provided updateResponseResource function from props
    if (updateResponseResource) {
      updateResponseResource(response, question.key, attempts, dispatch);
    }
  }

  renderMedia = () => {
    const { question } = this.props;
    if (question.mediaURL) {
      return (
        <div className='fill-in-blank-ell-illustration'>
          <img alt={question.mediaAlt} src={question.mediaURL} />
        </div>
      );
    }
  }

  getBlankFeedbackText = () => {
    const { question } = this.props;
    return staticStrings[question.blankAllowed ? UNMATCHED_OPTION_OR_BLANK_KEY : UNMATCHED_OPTION_KEY];
  }

  showNextQuestionButton = () => {
    const { question, mode, } = this.props;
    const latestAttempt = getLatestAttempt(question.attempts);
    const maxAttemptsReached = question.attempts && question.attempts.length === maxAttempts(mode, question);
    const optimalResponse = latestAttempt && latestAttempt.response.optimal;
    return maxAttemptsReached || optimalResponse;
  }

  renderButton = () => {
    const { isLastQuestion, language, mode, previewMode, question, showTranslation, translate, nextQuestion, languageLocale } = this.props;
    const { responses } = this.state;

    if (mode === DIAGNOSTIC || mode === TURK) {
      const buttonText = language && translate ? translate('buttons^submit') : 'Submit';
      const latestAttempt = getLatestAttempt(question.attempts);

      if ((previewMode && latestAttempt) || !responses) {
        return <button className="quill-button focus-on-light large green contained disabled" lang={languageLocale} type="button">{buttonText}</button>;
      }
      return <button className="quill-button focus-on-light large green contained" lang={languageLocale} onClick={this.handleSubmitClick} type="button">{buttonText}</button>;
    } else {
      // Connect mode
      const showRecheckWorkButton = question && question.attempts ? question.attempts.length > 0 : false;
      if (this.showNextQuestionButton()) {
        let buttonText = isLastQuestion ? 'Next' : 'Next question';
        buttonText = showTranslation && translate ? translate(`buttons^${buttonText.toLowerCase()}`) : buttonText;
        const disabledStyle = previewMode && isLastQuestion ? 'disabled' : '';
        return (
          <button className={`quill-button focus-on-light large green contained ${disabledStyle}`} lang={languageLocale} onClick={nextQuestion} type="button">{buttonText}</button>
        );
      } else if (responses) {
        if (showRecheckWorkButton) {
          const buttonText = showTranslation && translate ? translate('buttons^recheck work') : 'Recheck work';
          const matchesPreviousAttempt = this.inputsMatchPreviousAttempt();
          const disabledStyle = matchesPreviousAttempt ? 'disabled' : '';
          return <button className={`quill-button focus-on-light large green contained ${disabledStyle}`} disabled={matchesPreviousAttempt} lang={languageLocale} onClick={this.handleSubmitClick} type="button">{buttonText}</button>;
        } else {
          const buttonText = showTranslation && translate ? translate('buttons^submit') : 'Submit';
          return <button className="quill-button focus-on-light large green contained" lang={languageLocale} onClick={this.handleSubmitClick} type="button">{buttonText}</button>;
        }
      } else {
        const buttonText = showTranslation && translate ? translate('buttons^submit') : 'Submit';
        return <button className="quill-button focus-on-light large green contained disabled" lang={languageLocale} type="button">{buttonText}</button>;
      }
    }
  }

  renderRemainingAttemptCount = () => {
    const { mode, question, } = this.props
    if (mode !== GRAMMAR || this.showNextQuestionButton()) { return }

    const attemptsMade = question.attempts?.length || 0
    const remainingAttempts = maxAttempts(mode, question) - attemptsMade

    return (<span className="remaining-attempt-count">{remainingAttempts} {pluralize(remainingAttempts, 'try', 'tries')} left</span>)
  }

  renderFeedback = () => {
    const { diagnosticID, FeedbackContainer, language, mode, previewMode, question, showTranslation, translate, languageLocale } = this.props;
    const { inputErrors, responses } = this.state;

    const latestAttempt = getLatestAttempt(question.attempts);

    if (mode === DIAGNOSTIC || mode === TURK) {
      const errorsPresent = _.size(inputErrors) !== 0;
      if (errorsPresent) {
        const translationKey = question.blankAllowed ? 'feedback^blank' : 'feedback^no blank';
        const feedbackText = this.getBlankFeedbackText();
        if (language && diagnosticID !== 'ell') {
          const translatedFeedbackText = language !== ENGLISH && translate ? translate(translationKey) : undefined;
          return (
            <Feedback
              feedbackText={feedbackText}
              feedbackType="revise-unmatched"
              language={language}
              languageLocale={languageLocale}
              translatedFeedbackText={translatedFeedbackText}
            />
          );
        }
        return (
          <Feedback
            feedbackText={feedbackText}
            feedbackType="revise-unmatched"
          />
        );
      }
      if (previewMode && latestAttempt && latestAttempt.response) {
        return renderPreviewFeedback(latestAttempt);
      } else {
        return this.renderDiagnosticInstructionsFeedback();
      }
    } else {
      const maxAttemptsSubmitted = question.attempts && question.attempts.length === maxAttempts(mode, question);

      if (maxAttemptsSubmitted && latestAttempt && !latestAttempt.response.optimal) {
        return (
          <FinalAttemptFeedback
            correctResponse={this.correctResponse()}
            languageLocale={languageLocale}
            latestAttempt={latestAttempt.response.text}
          />
        );
      } else if (inputErrors && _.size(inputErrors) !== 0) {
        return (
          <Feedback
            feedbackText={this.getBlankFeedbackText()}
            feedbackType="revise-unmatched"
            languageLocale={languageLocale}
            latestAttempt={latestAttempt}
            showTranslation={showTranslation}
            translate={translate}
          />
        );
      }

      if (FeedbackContainer) {
        return (
          <FeedbackContainer
            languageLocale={languageLocale}
            latestAttempt={latestAttempt}
            previewMode={previewMode}
            question={question}
            responses={responses}
            sentence={this.getInstructionText()}
            showTranslation={showTranslation}
            translate={translate}
          />
        );
      }
      return null;
    }
  }

  renderCues = () => {
    const { diagnosticID, language, mode, question, translate, languageLocale } = this.props;

    return (
      <Cues
        diagnosticID={diagnosticID}
        displayArrowAndText={true}
        includeReadAloudButton={true}
        language={language}
        languageLocale={languageLocale}
        question={question}
        translate={translate}
      />
    );
  }

  getConcept = () => {
    const { concepts, question } = this.props;
    if (!concepts || !concepts.data) {
      return null;
    }
    const conceptID = question.conceptID || question.concept_uid;
    return concepts.data[conceptID];
  }

  example = (): string | void => {
    const { question } = this.props;
    if (question.ruleDescription && question.ruleDescription.length && question.ruleDescription !== "<br/>") {
      return question.ruleDescription;
    } else {
      const concept = this.getConcept();
      if (concept && concept.explanation) {
        return concept.explanation;
      }
    }
  }

  renderExample = (): JSX.Element | undefined => {
    const { mode } = this.props;

    // Only show example for Grammar mode
    if (mode !== GRAMMAR) {
      return undefined;
    }

    const { showExample } = this.state;
    const example = this.example();
    if (example) {
      let componentClasses = 'example-container flex-start';
      if (showExample) {
        componentClasses += ' show';
      }
      return (
        <div className={componentClasses}>
          <div className="example" dangerouslySetInnerHTML={{ __html: example.replace(/\n/g, "<br />") }} />
        </div>
      );
    } else {
      return undefined;
    }
  }

  render() {
    const { language, question } = this.props;

    let fullPageInstructions;
    if (language === 'arabic' && !(question.mediaURL)) {
      fullPageInstructions = { maxWidth: 800, width: '100%' };
    } else {
      fullPageInstructions = { display: 'block', width: '100%' };
    }

    return (
      <ReadAloudSource source={clipSource(CLIP_SOURCE_TYPE.QUESTION, question.key)}>
        <div className="student-container-inner-diagnostic fill-in-blank-container">
          <div className="fill-in-blank-layout-flex">
            <div style={fullPageInstructions}>
              {this.renderExample()}
              <div className="flex-column">
                <span className="sr-only">Screenreader users: make sure to read through the whole sentence before filling in the blanks.</span>
                <FillInBlankPromptWithReadAloud prompt={question.prompt} promptElements={this.getPromptElements()} />
                {this.renderCues()}
                {this.renderFeedback()}
              </div>
            </div>
            {this.renderMedia()}
          </div>
          <div className="question-button-group button-group fill-in-blank-button-container">
            <div className="button-and-remaining-attempt-wrapper">
              {this.renderButton()}
              {this.renderRemainingAttemptCount()}
            </div>
          </div>
          {this.renderConceptExplanation()}
        </div>
      </ReadAloudSource>
    );
  }
}

export default PlayFillInBlankQuestion;
